/**
 * A column head you can sort by.
 *
 * Was copied character for character into the scans table and the leads table,
 * and the register list would have been the third copy (AGENTS.md §4). It is a
 * `<button>` inside a `crm-list-head` rather than inside a `<th>`, because these
 * lists are grids of pills and not tables - but it keeps `aria-sort`, so the
 * direction is announced and not only drawn as an arrow.
 *
 * The button inherits the head's own typography (`font-[inherit]`, uppercase,
 * the head's letter-spacing) instead of setting its own: a sortable head and a
 * plain one sit next to each other in the same row and must look like the same
 * kind of label, with only the hover and the arrow telling them apart.
 */
import { ArrowDown, ArrowUp } from "lucide-react";
import { cn } from "@wa/ui";

export function SortHead<T extends string>({
  label,
  column,
  sort,
  order,
  onSort,
  className,
}: {
  label: string;
  /** Which sort key this head asks for. */
  column: T;
  /** The sort in force, for the whole list. */
  sort: T;
  order: "asc" | "desc";
  /** Called with this head's column. Flipping the direction when it is already
   *  the active one is the caller's decision, not this component's - it depends
   *  on which way that column naturally reads first. */
  onSort: (c: T) => void;
  className?: string;
}) {
  const active = sort === column;
  return (
    <span aria-sort={active ? (order === "asc" ? "ascending" : "descending") : "none"} className={className}>
      <button
        type="button"
        onClick={() => onSort(column)}
        className={cn(
          "inline-flex max-w-full items-center gap-1 font-[inherit] uppercase tracking-[inherit] transition-colors hover:text-foreground",
          active && "text-foreground",
        )}
      >
        <span className="crm-trunc">{label}</span>
        {active ? (
          order === "asc" ? (
            <ArrowUp className="size-3 shrink-0" />
          ) : (
            <ArrowDown className="size-3 shrink-0" />
          )
        ) : null}
      </button>
    </span>
  );
}
